import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnVisibilityState } from '@tanstack/react-table';

import { Spinner } from '@/components/atoms/Spinner';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { useGetAllCages } from '@/domain/cages';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';
import { mergeColumnVisibility } from '@/utils/columnVisibility';
import { getFromStorage, setToStorage } from '@/utils/localStorage';

import CagesListView, { type CageStatusCounts } from './CagesListView';

const COLUMN_VISIBILITY_STORAGE_KEY = 'cage-list-column-visibility';
const PAGE_SIZE_STORAGE_KEY = 'cage-list-page-size';
const DEFAULT_PAGE_SIZE = 15;
const DEFAULT_VISIBLE_COLUMNS: ColumnVisibilityState = {
    id: true,
    name: true,
    fishGroup: true,
    status: true,
    client: true,
    location: true,
    cageType: false,
    fishType: false,
    externalId: false,
    externalName: false,
    currentBioscopeId: true,
    bioscopeHistory: true,
    historicalAt: false,
    firstMeasurement: false,
    lastMeasurement: true,
};

/**
 * The column visibility to start from, given whatever the user has stored.
 *
 * Stored preferences predate any column added since they were written, and TanStack treats a
 * column that is absent from the visibility state as VISIBLE. Reading storage on its own
 * therefore makes every new column appear for existing users regardless of its default, which
 * is how the fish type column shipped switched on. Layering the stored value over the defaults
 * gives an unseen column its intended default while keeping every explicit choice.
 */
export function resolveColumnVisibility(stored: ColumnVisibilityState): ColumnVisibilityState {
    return mergeColumnVisibility(DEFAULT_VISIBLE_COLUMNS, stored);
}

interface FilterOption {
    value: string;
    label: string;
}

const DEFAULT_CAGE_STATUS_COUNTS: CageStatusCounts = {
    active: 0,
    inactive: 0,
    historical: 0,
};

export default function CagesListContainer() {
    const { t } = useTranslation();
    const cagesQuery = useGetAllCages();
    const { enabled: fishGroupsEnabled } = usePostHogFeatureFlag('fish-group-tracking');
    const [filter, setFilter] = useState('');
    const [selectedClientId, setSelectedClientId] = useState('all');
    const [selectedLocationId, setSelectedLocationId] = useState('all');
    const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(() => {
        return resolveColumnVisibility(
            getFromStorage<ColumnVisibilityState>(COLUMN_VISIBILITY_STORAGE_KEY, {})
        );
    });
    const [pageSize, setPageSize] = useState<number>(() => {
        return getFromStorage(PAGE_SIZE_STORAGE_KEY, DEFAULT_PAGE_SIZE);
    });

    useEffect(() => {
        setToStorage(COLUMN_VISIBILITY_STORAGE_KEY, columnVisibility);
    }, [columnVisibility]);

    useEffect(() => {
        setToStorage(PAGE_SIZE_STORAGE_KEY, pageSize);
    }, [pageSize]);

    const clientOptions = useMemo<FilterOption[]>(() => {
        const uniqueClients = new Map<number, string>();

        for (const cage of cagesQuery.data ?? []) {
            if (cage.clientId && cage.clientName) {
                uniqueClients.set(cage.clientId, cage.clientName);
            }
        }

        return [
            { value: 'all', label: t('internal.cages.allClients') },
            ...Array.from(uniqueClients.entries())
                .map(([id, name]) => ({ value: String(id), label: name }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ];
    }, [cagesQuery.data, t]);

    const locationOptions = useMemo<FilterOption[]>(() => {
        const uniqueLocations = new Map<number, string>();
        const selectedClientIdNumber =
            selectedClientId === 'all' ? null : Number.parseInt(selectedClientId, 10);

        for (const cage of cagesQuery.data ?? []) {
            if (!cage.locationId || !cage.locationName) {
                continue;
            }

            if (selectedClientIdNumber && cage.clientId !== selectedClientIdNumber) {
                continue;
            }

            uniqueLocations.set(cage.locationId, cage.locationName);
        }

        return [
            { value: 'all', label: t('internal.cages.allLocations') },
            ...Array.from(uniqueLocations.entries())
                .map(([id, name]) => ({ value: String(id), label: name }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ];
    }, [cagesQuery.data, selectedClientId, t]);

    useEffect(() => {
        const hasSelectedLocation = locationOptions.some(
            (locationOption) => locationOption.value === selectedLocationId
        );

        if (!hasSelectedLocation) {
            setSelectedLocationId('all');
        }
    }, [locationOptions, selectedLocationId]);

    const filteredCages = useMemo(() => {
        const selectedClientIdNumber =
            selectedClientId === 'all' ? null : Number.parseInt(selectedClientId, 10);
        const selectedLocationIdNumber =
            selectedLocationId === 'all' ? null : Number.parseInt(selectedLocationId, 10);

        return (cagesQuery.data ?? []).filter((cage) => {
            if (selectedClientIdNumber && cage.clientId !== selectedClientIdNumber) {
                return false;
            }

            if (selectedLocationIdNumber && cage.locationId !== selectedLocationIdNumber) {
                return false;
            }

            return true;
        });
    }, [cagesQuery.data, selectedClientId, selectedLocationId]);

    const cageStatusCounts = useMemo<CageStatusCounts>(() => {
        return filteredCages.reduce<CageStatusCounts>(
            (counts, cage) => ({
                ...counts,
                [cage.status]: counts[cage.status] + 1,
            }),
            DEFAULT_CAGE_STATUS_COUNTS
        );
    }, [filteredCages]);

    const hasActiveFilters =
        filter.trim().length > 0 || selectedClientId !== 'all' || selectedLocationId !== 'all';

    const handleClearFilters = () => {
        setFilter('');
        setSelectedClientId('all');
        setSelectedLocationId('all');
    };

    // The list carries the page's title, so the error state stands in as its h1.
    if (cagesQuery.error) {
        return <SomethingHappened headingLevel="h1" {...retryFailedQueries(cagesQuery)} />;
    }

    if (cagesQuery.isLoading) {
        return <Spinner size="lg" fullScreen />;
    }

    return (
        <CagesListView
            cages={filteredCages}
            cageStatusCounts={cageStatusCounts}
            filter={filter}
            onFilterChange={setFilter}
            selectedClientId={selectedClientId}
            onSelectedClientIdChange={setSelectedClientId}
            clientOptions={clientOptions}
            selectedLocationId={selectedLocationId}
            onSelectedLocationIdChange={setSelectedLocationId}
            locationOptions={locationOptions}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={handleClearFilters}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            fishGroupsEnabled={fishGroupsEnabled}
        />
    );
}
